import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import { personSchema } from "@/components/Forms/schema/personSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "person",
    attributesSchema: personSchema,
    documentMetaSchema: dtMetaSchema,
});

export type PeopleDocument = ReturnType<typeof resourceSelector>;
export type MrtPerson = PeopleDocument["data"][number];

type Props = {
    isQueryEnabled?: boolean;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtPeople = ({
    isQueryEnabled = true,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<PeopleDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "people-table-data",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                sorting: sorting ?? [],
            };

            const response = await fetch(apiUrl("/person"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: resourceSelector,
    });
};
